import React, { useMemo } from "react";
import { Button, buttonVariants } from "@shared/ui/button";
import { Badge } from "@shared/ui/badge";
import {
  Layers,
  Calendar,
  Clock,
  ArrowRight,
  Link2,
  Receipt,
  CircleDollarSign,
  BarChart3,
  AlertCircle,
} from "lucide-react";
import { useParams, Link } from "react-router-dom";
import {
  useCategoryOptions,
  useGetParameter,
  useGetPlanDetail,
  useGetPlanDetailByTab,
  useUpdatePlan,
  useUtilityServices,
} from "../../../hooks";
import { useCurrency } from "@shared/selectors/globalSelectors";


interface ReviewProps {
  data?: any;
  allStepData?: any;
  onDataChange?: (data: any) => void;
  onNext?: () => void;
  onPrevious?: () => void;
  isFirstStep?: boolean;
  isLastStep?: boolean;
  stepHelpers?: any;
}

const DiffTableCell = ({ oldVal, newVal }: { oldVal: any; newVal: any }) => {
  const format = (v: any) => {
    if (v === undefined || v === null || v === "") return undefined;
    if (Array.isArray(v)) return v.join(", ");
    return String(v);
  };

  const o = format(oldVal);
  const n = format(newVal);

  if (o === n) {
    return <span className="text-gray-700 text-sm">{o || "-"}</span>;
  }
  return (
    <div className="flex flex-col text-sm">
      {o !== undefined && (
        <span className="text-red-500 line-through text-xs mb-0.5">{o}</span>
      )}
      {n !== undefined && (
        <span className="text-green-600 font-medium text-xs">{n}</span>
      )}
    </div>
  );
};

import { GenericTable } from "@shared/components/GenericTable";

const formatModeLabel = (m: string) => {
  if (!m || m === "N/A") return "";
  if (m === "parameter-wise" || m === "parameter_wise") return "parameterWise";
  return m;
};

const DiffTable = ({ title, mode, data, columns }: any) => {
  const normalizeForCompare = (v: any): string => {
    if (v === undefined || v === null || v === "") return "";
    if (Array.isArray(v)) return v.map((x) => String(x).trim()).join(", ");
    return String(v).trim();
  };

  const changedData = data.filter((row: any) => {
    return columns.some((c: any) => {
      const o = normalizeForCompare(row.old?.[c.key]);
      const n = normalizeForCompare(row.new?.[c.key]);
      return o !== n;
    });
  });

  const changesCount = changedData.length;

  const genericColumns = columns.map((c: any) => ({
    key: c.key,
    header: c.label,
    render: (_: any, row: any) => (
      <DiffTableCell oldVal={row.old?.[c.key]} newVal={row.new?.[c.key]} />
    ),
  }));

  if (changesCount === 0) return null;

  const displayMode = formatModeLabel(mode);

  return (
    <div className="mb-6 border border-gray-200 rounded-lg overflow-hidden shadow-sm bg-white">
      <div className="px-5 py-3 bg-gray-50/50 border-b border-gray-200 flex justify-between items-center">
        <div className="flex items-center gap-3">
          <span className="font-semibold text-gray-800 text-sm">{title}</span>
          {displayMode && (
            <Badge
              variant="outline"
              className="text-blue-600 bg-blue-50 border-blue-200 rounded-full px-2 py-0 h-5 text-[10px] font-medium tracking-wider"
            >
              {displayMode}
            </Badge>
          )}
        </div>
        <Badge className="bg-amber-100 text-amber-800 border-amber-200 font-medium hover:bg-amber-100 px-2 py-0.5">
          {changesCount} changed
        </Badge>
      </div>
      <div className="p-0">
        <GenericTable
          data={changedData}
          columns={genericColumns}
          rowKey={(row: any) => JSON.stringify(row)}
        />
      </div>
    </div>
  );
};


const DiffCard = ({ title, data }: { title: string; data: any[] }) => {
  const format = (v: any) => {
    if (v === undefined || v === null || v === "") return undefined;
    if (Array.isArray(v)) return v.join(", ");
    return String(v);
  };

  const changedData = data.filter((item) => {
    return format(item.old.value) !== format(item.new.value);
  });

  if (changedData.length === 0) return null;

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden mb-6">
      <div className="px-5 py-4 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-800">{title}</h3>
      </div>
      <div className="p-5">
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-6">
          {changedData.map((item, idx) => {
            const oldVal = item.old.value;
            const newVal = item.new.value;
            const o = format(oldVal);
            const n = format(newVal);
            return (
              <div key={idx} className="flex flex-col">
                <dt className="text-xs font-semibold text-gray-400 tracking-wider mb-1.5">
                  {item.new.field}
                </dt>
                <dd className="text-sm flex flex-col items-start">
                  <>
                    {o !== undefined && (
                      <span className="text-red-500 line-through text-xs mb-0.5">
                        {o}
                      </span>
                    )}
                    {n !== undefined && (
                      <span className="text-green-600 font-medium">{n}</span>
                    )}
                  </>
                </dd>
              </div>
            );
          })}
        </dl>
      </div>
    </div>
  );
};

const pairArraysByName = (
  oldArr: any[] = [],
  newArr: any[] = [],
  getName: (item: any) => string = (item) =>
    item?.taxName ||
    item?.tax_name ||
    item?.chargeName ||
    item?.charge_name ||
    item?.surchargeName ||
    item?.surcharge_name ||
    item?.name ||
    item?.deposit_name ||
    item?.depositName ||
    item?.penalty_name ||
    item?.penaltyName ||
    ""
) => {
  const paired: { old: any; new: any }[] = [];
  const usedOldIndices = new Set<number>();

  newArr.forEach((newItem) => {
    const newName = getName(newItem)?.trim().toLowerCase();
    let oldIndex = -1;
    if (newName) {
      oldIndex = oldArr.findIndex(
        (oldItem, idx) =>
          !usedOldIndices.has(idx) &&
          getName(oldItem)?.trim().toLowerCase() === newName
      );
    }
    if (oldIndex !== -1) {
      usedOldIndices.add(oldIndex);
      paired.push({ old: oldArr[oldIndex], new: newItem });
    } else {
      paired.push({ old: {}, new: newItem });
    }
  });

  oldArr.forEach((oldItem, idx) => {
    if (!usedOldIndices.has(idx)) {
      paired.push({ old: oldItem, new: {} });
    }
  });

  return paired;
};


export const Review: React.FC<ReviewProps> = ({
  allStepData: propsAllStepData,
  onNext,
  onPrevious,
  isFirstStep,
  stepHelpers,
}) => {
  const currency = useCurrency();
  const { planId } = useParams();
  const { data: planDetail } = useGetPlanDetail(planId);
  const { data: planDetailRates } = useGetPlanDetailByTab(
    planId || "",
    "rates",
  );
  const { mutate: updatePlan, isPending } = useUpdatePlan();
  const { data: utilityServiceData } = useUtilityServices();

  const allStepData = useMemo(() => {
    if (propsAllStepData && Object.keys(propsAllStepData).length > 0)
      return propsAllStepData;
    const stored = localStorage.getItem(`plan-edit-progress-${planId}`);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        return parsed.stepData || parsed || {};
      } catch (e) {
        return {};
      }
    }
    return {};
  }, [propsAllStepData, planId]);

  console.log("allStepData", allStepData);

  const normalizeData = (src: any, isApi: boolean, apiRates?: any): any => {

    if (!src)
      return {
        basic: {
          planName: "",
          planCode: "",
          effectiveDate: "",
          endDate: "",
          description: "",
          billingCycle: 1,
          validateForm: "",
          validatTo: "",
        },
        categories: { categories: [], subCategories: [], contracts: [] },
        rates: {},
        charges: {
          taxMode: "common",
          fixedChargeMode: "parameterWise",
          surchargeMode: "common",
          linkedCharges: [],
          taxes: [],
          fixedCharges: [],
          surcharges: [],
        },
        penalties: { enabled: false, mode: "common", rules: [] },
        deposits: { enabled: false, mode: "common", rules: [] },
      };


    // Common helpers
    const getArr = (v: any) => (Array.isArray(v) ? v : []);

    // Field access helpers
    const basic = isApi ? src : src[0] || src["basic-info"] || src.basic || {};
    const cat = isApi
      ? src
      : src[1] || src["plan-config"] || src.categories || {};

    let apiParsedRates: any = {};
    if (isApi) {
      if (apiRates?.ratesByService) {
        Object.entries(apiRates.ratesByService).forEach(
          ([serviceKey, rateObj]: [string, any]) => {
            const serviceName = serviceKey
              .split(" ")
              .map(
                (w: string) =>
                  w.charAt(0).toUpperCase() + w.slice(1).toLowerCase(),
              )
              .join(" ");
            apiParsedRates[serviceName] = {
              tariff: rateObj.rateId?.toString() || rateObj.rate?.toString(),
              tariffName: rateObj.rateName || rateObj.name,
              rateAmount:
                rateObj.rateAmount || rateObj.tariffAmount || rateObj.rate,
              avgConsumption: rateObj.avgConsumption,
            };
          },
        );
      }
      if (src?.tariff_config?.selected_tariffs) {
        Object.entries(src.tariff_config.selected_tariffs).forEach(
          ([serviceKey, rateObj]: [string, any]) => {
            const serviceName = serviceKey
              .split(" ")
              .map(
                (w: string) =>
                  w.charAt(0).toUpperCase() + w.slice(1).toLowerCase(),
              )
              .join(" ");
            if (!apiParsedRates[serviceName]) {
              apiParsedRates[serviceName] = {
                tariff:
                  rateObj.rate_id?.toString() ||
                  rateObj.rateId?.toString() ||
                  rateObj.rate,
                tariffName:
                  rateObj.rate_name || rateObj.rateName || rateObj.name,
                rateAmount:
                  rateObj.rate_amount || rateObj.rateAmount || rateObj.amount,
                avgConsumption:
                  rateObj.avg_consumption || rateObj.avgConsumption,
              };
            }
          },
        );
      }
      if (apiRates?.conditionalRateRules) {
        Object.entries(apiRates.conditionalRateRules).forEach(
          ([serviceKey, rule]: [string, any]) => {
            const serviceName = serviceKey
              .split(" ")
              .map(
                (w: string) =>
                  w.charAt(0).toUpperCase() + w.slice(1).toLowerCase(),
              )
              .join(" ");
            apiParsedRates[serviceName] = {
              ...apiParsedRates[serviceName],
              isConditional: rule.enabled,
              parameter: rule.parameterId,
              condition: rule.operator,
              value: rule.thresholdValue,
              tariffBelow: rule.belowRate?.rateId,
              tariffBelowName: rule.belowRate?.rateName,
              tariffAbove: rule.aboveRate?.rateId,
              tariffAboveName: rule.aboveRate?.rateName,
            };
          },
        );
      }
    }

    const rates = isApi
      ? apiParsedRates
      : src[2]?.rates || src["select-rates"]?.rates || src.rates || {};
    const charges = isApi
      ? src.chargesConfig || src.charges_config || {}
      : src[3] || src["charges-taxes"] || src.charges || {};
    const dep = isApi
      ? src.depositsConfig || src.deposits_config || {}
      : src[4] || src["deposits"] || src.deposits || {};
    const pen = isApi
      ? src.penaltiesConfig || src.penalties_config || {}
      : src[5] || src["penalties"] || src.penalties || {};

    const penRules =
      pen.rules ||
      pen.rulesGrouped?.flatMap((g: any) => g.rules) ||
      pen.rules_grouped?.flatMap((g: any) => g.rules) ||
      pen.penalties ||
      [];

    let depRules =
      dep.rules ||
      dep.rulesGrouped?.flatMap((g: any) => g.rules) ||
      dep.rules_grouped?.flatMap((g: any) => g.rules) ||
      [];
    if (!isApi && dep.deposits) {
      if (dep.mode === "common" && dep.deposits.common) {
        depRules = [dep.deposits.common];
      } else if (
        (dep.mode === "parameter-wise" || dep.mode === "parameter_wise") &&
        dep.deposits.parameterWise?.rules
      ) {
        depRules = dep.deposits.parameterWise.rules;
      }
    }

    const formatNumDate = (dateStr: any) => {
      if (!dateStr || dateStr === "Not set" || dateStr === "") return "Not set";
      const str = String(dateStr).trim();
      const cleanStr = str.split("T")[0];
      const parts = cleanStr.split(/[-/]/);
      if (parts.length === 3) {
        if (parts[0].length === 4) {
          return `${parts[2].padStart(2, "0")}/${parts[1].padStart(2, "0")}/${parts[0]}`;
        }
        if (parts[2].length === 4) {
          return `${parts[0].padStart(2, "0")}/${parts[1].padStart(2, "0")}/${parts[2]}`;
        }
      }
      const dateObj = new Date(str);
      if (!isNaN(dateObj.getTime())) {
        const dd = String(dateObj.getDate()).padStart(2, "0");
        const mm = String(dateObj.getMonth() + 1).padStart(2, "0");
        const yyyy = dateObj.getFullYear();
        return `${dd}/${mm}/${yyyy}`;
      }
      return str;
    };

    const toStrArr = (v: any): string[] => {
      if (!v) return [];
      const arr = Array.isArray(v) ? v.flat() : [v];
      const items: string[] = [];
      arr.forEach((x: any) => {
        const valStr =
          typeof x === "object" && x !== null
            ? (x.value ?? x.label ?? String(x))
            : String(x);
        if (valStr) {
          valStr.split(",").forEach((s: string) => {
            const trimmed = s.trim();
            if (trimmed && trimmed.toLowerCase() !== "all") {
              items.push(trimmed);
            }
          });
        }
      });
      return Array.from(new Set(items)).sort();
    };

    const formatTypeLabel = (type: any) => {
      if (!type || type === "-") return "-";
      const str = String(type).toLowerCase().trim();
      if (str.includes("percent") || str.includes("%"))
        return "Percentage %";
      if (
        str.includes("flat") ||
        str.includes("amount") ||
        str === "fixed"
      ) {
        return currency ? `Flat ${currency}` : "Flat Rate";
      }
      return type;
    };

    const formatChargeRateOrAmount = (item: any) => {
      const typeStr = String(item.type || "").toLowerCase().trim();
      const isFlat =
        typeStr.includes("flat") ||
        typeStr.includes("amount") ||
        typeStr === "fixed";

      if (isFlat) {
        const amt =
          item.amount != null && item.amount !== "" && Number(item.amount) !== 0
            ? item.amount
            : item.rate != null && item.rate !== ""
              ? item.rate
              : item.amount ?? "-";
        if (amt === "-") return "-";
        return currency ? `${amt} ${currency}` : String(amt);
      } else {
        const rt =
          item.rate != null && item.rate !== "" && Number(item.rate) !== 0
            ? item.rate
            : item.amount != null && item.amount !== ""
              ? item.amount
              : item.rate ?? "-";
        if (rt === "-") return "-";
        return `${rt}%`;
      }
    };

    return {
      basic: {
        planName: basic.planName || basic.plan_name || "Not set",
        planCode: basic.planCode || basic.plan_code || "Not set",
        effectiveDate: isApi
          ? formatNumDate(
              basic.effectiveDate || basic.effective_date || basic.start_date,
            )
          : formatNumDate(
              basic.validateForm ||
                basic.effectiveDate ||
                basic.effective_date,
            ),
        endDate: isApi
          ? formatNumDate(basic.endDate || basic.end_date || basic.validity)
          : formatNumDate(basic.validatTo || basic.endDate || basic.end_date),

        // Preserve original fields for payload generation
        description: basic.description || "",
        billingCycle: basic.billingCycle || basic.billing_frequency || 1,
        validateForm: isApi
          ? basic.effectiveDate || basic.effective_date
          : basic.validateForm,
        validatTo: isApi ? basic.endDate || basic.end_date : basic.validatTo,
      },
      categories: {
        categories: isApi
          ? getArr(
              src.categoriesRaw ||
                src.categories?.map((c: any) => c.code || c.name),
            )
          : getArr(cat.categories || cat.consumerCategories),
        subCategories: isApi
          ? getArr(
              src.subCategoriesRaw ||
                src.subCategories?.map((c: any) => c.code || c.name),
            )
          : getArr(cat.subCategories),
        contracts: isApi
          ? getArr(src.contractIds || src.contract_ids)
          : getArr(cat.contracts),
      },
      rates: rates,
      charges: {
        taxMode: charges.taxMode || (charges.taxes?.[0]?.mode),
        fixedChargeMode: charges.fixedChargeMode || (charges.fixedCharges?.[0]?.mode || charges.fixed_charges?.[0]?.mode),
        surchargeMode: charges.surchargeMode || (charges.surcharges?.[0]?.mode),
        linkedCharges: getArr(
          charges.linkedCharges || charges.linked_charges,
        ).map((c: any) => {
          const rawRate = c.chargeRate ?? c.charge_rate;
          let chargeUnit: string;
          if (Array.isArray(rawRate)) {
            chargeUnit = rawRate
              .map((r: any) => `${r.rate} / ${r.unit}`)
              .join(", ");
          } else if (rawRate != null && rawRate !== "") {
            chargeUnit = String(rawRate);
          } else {
            chargeUnit =
              c.chargeUnit != null && c.chargeUnit !== ""
                ? String(c.chargeUnit)
                : c.charge_unit != null && c.charge_unit !== ""
                  ? String(c.charge_unit)
                  : "-";
          }
          return {
            name: c.name || "-",
            linkedService: c.linkedService || c.linked_service || "-",
            usage: c.usage != null && c.usage !== "" ? String(c.usage) : "-",
            chargeUnit,
          };
        }),
        taxes: getArr(charges.taxes).map((t: any) => {
          const rawUtilSvc =
            t.utilityServices || t.utility_services || t.utilityService;
          const rawVal = t.parameterValues || t.parameter_values || t.value;
          const paramId =
            t.parameterId || t.parameter_id || t.parameter || "";
          const val = formatChargeRateOrAmount(t);
          return {
            ...t,
            taxName: t.taxName || t.tax_name || t.name || "-",
            type: formatTypeLabel(t.type),
            rate: val,
            amount: val,
            utilityService: toStrArr(rawUtilSvc).join(", ") || "-",
            parameter: paramId ? String(paramId) : "-",
            value: toStrArr(rawVal).join(", ") || "All",
          };
        }),
        fixedCharges: getArr(charges.fixedCharges || charges.fixed_charges).map(
          (f: any) => {
            const rawUtilSvc =
              f.utilityServices || f.utility_services || f.utilityService;
            const rawVal = f.parameterValues || f.parameter_values || f.value;
            const paramId =
              f.parameterId || f.parameter_id || f.parameter || "";
            const amt = f.amount != null && f.amount !== "" ? f.amount : "-";
            const amtStr = amt !== "-" && currency ? `${amt} ${currency}` : String(amt);
            return {
              utilityService: toStrArr(rawUtilSvc).join(", ") || "-",
              parameter: paramId ? String(paramId) : "-",
              value: toStrArr(rawVal).join(", ") || "All",
              chargeName: f.chargeName || f.charge_name || f.name || "-",
              amount: amtStr,
              includeIn: f.includeIn || f.include_in || "Every bill",
            };
          },
        ),
        surcharges: getArr(charges.surcharges).map((s: any) => {
          const rawUtilSvc =
            s.utilityServices || s.utility_services || s.utilityService;
          const rawVal = s.parameterValues || s.parameter_values || s.value;
          const paramId =
            s.parameterId || s.parameter_id || s.parameter || "";
          const val = formatChargeRateOrAmount(s);
          return {
            ...s,
            surchargeName:
              s.surchargeName ||
              s.surcharge_name ||
              s.chargeName ||
              s.name ||
              "-",
            type: formatTypeLabel(s.type),
            rate: val,
            amount: val,
            utilityService: toStrArr(rawUtilSvc).join(", ") || "-",
            parameter: paramId ? String(paramId) : "-",
            value: toStrArr(rawVal).join(", ") || "All",
          };
        }),
      },
      penalties: {
        enabled: pen.enabled || pen.enablePenalties || false,
        mode: pen.mode || penRules[0]?.mode || "common",
        rules: penRules.map((r: any) => {
          const rawUtilSvc =
            r.utilityService || r.utilityServices || r.utility_services;
          const rawVal = r.parameterValues || r.parameter_values || r.value;
          const paramId =
            r.parameterId || r.parameter_id || r.parameter || "";

          const penaltyName =
            r.penaltyName || r.penalty_name || r.name || "-";
          const chargeType =
            r.chargeType || r.charge_type || r.type || "flat";
          const graceDays =
            r.graceDays != null && r.graceDays !== ""
              ? String(r.graceDays)
              : r.grace != null && r.grace !== ""
                ? String(r.grace)
                : r.grace_days != null && r.grace_days !== ""
                  ? String(r.grace_days)
                  : "-";
          const rawAmt = r.amount != null && r.amount !== "" ? r.amount : "-";

          const formattedType = formatTypeLabel(chargeType);
          const formattedAmt =
            rawAmt !== "-" && (chargeType === "flat" || chargeType === "amount") && currency
              ? `${rawAmt} ${currency}`
              : String(rawAmt);

          return {
            ...r,
            penalty_name: penaltyName,
            charge_type: formattedType,
            grace_days: graceDays,
            amount: formattedAmt,
            utility_services: toStrArr(rawUtilSvc).join(", ") || "-",
            parameter: paramId ? String(paramId) : "-",
            parameter_values: toStrArr(rawVal).join(", ") || "All",
          };
        }),
      },
      deposits: {
        enabled: dep.enabled || dep.requireDeposit || false,
        mode: dep.mode || depRules[0]?.mode || "common",
        rules: depRules.map((r: any) => {
          const rawUtilSvc =
            r.utilityService || r.utilityServices || r.utility_services;
          const rawVal = r.parameterValues || r.parameter_values || r.value;
          const paramId =
            r.parameterId || r.parameter_id || r.parameter || "";

          const depositName =
            r.depositName || r.deposit_name || r.ruleType || "-";
          const rawAmount = r.depositAmount ?? r.amount ?? "-";
          const amountStr =
            rawAmount !== "-" && currency
              ? `${rawAmount} ${currency}`
              : String(rawAmount);

          const payInterest =
            r.payInterest !== undefined
              ? r.payInterest === "yes" || r.payInterest === true
                ? "Yes"
                : "No"
              : r.pay_interest !== undefined
                ? r.pay_interest
                  ? "Yes"
                  : "No"
                : "-";

          const eligibleAfter =
            r.eligibleAfter != null && r.eligibleAfter !== ""
              ? String(r.eligibleAfter)
              : r.eligible_after != null && r.eligible_after !== ""
                ? String(r.eligible_after)
                : "-";

          return {
            ...r,
            deposit_name: depositName,
            amount: amountStr,
            pay_interest: payInterest,
            eligible_after: eligibleAfter,
            utility_services: toStrArr(rawUtilSvc).join(", ") || "-",
            parameter: paramId ? String(paramId) : "-",
            parameter_values: toStrArr(rawVal).join(", ") || "All",
          };
        }),
      },
    };
  };

  console.log("normalize data:", normalizeData(allStepData, false));

  const handleSaveChanges = () => {
    const rawData = normalizeData(allStepData, false);
    const basic = rawData.basic || {};
    const cat = rawData.categories || {};
    const rates = rawData.rates || {};
    const charges = rawData.charges || {};
    const dep = rawData.deposits || {};
    const pen = rawData.penalties || {};
    const payload = {
      is_bulk_update: 1,
      is_version_change: 1,
      plan_name: basic.planName || "",
      short_name: basic.planCode || "",
      ...(basic.description && { description: basic.description }),
      billing_frequency: parseInt(basic.billingCycle) || 1,
      start_date: basic.validateForm || "",
      ...(basic.validatTo && { validity: basic.validatTo }),
      remote_utility_id: planDetail?.result?.remoteUtilityId,
      categories: cat.categories || [],
      sub_categories: cat.subCategories || [],
      contract_ids: cat.contracts || [],
      tariff_config: {
        services: Object.keys(rates),
        selected_tariffs: Object.entries(rates).reduce(
          (acc: any, [service, rate]: [string, any]) => {
            if (rate.tariff) {
              acc[service] = {
                rate_id: parseInt(rate.tariff) || rate.tariff,
                avg_consumption: parseFloat(rate.avgConsumption) || 0,
              };
            }
            return acc;
          },
          {},
        ),
        tariff_selection_rules: Object.entries(rates).reduce(
          (acc: any, [service, rate]: [string, any]) => {
            if (rate.tariffBelow && rate.tariffAbove) {
              const utilityObj = utilityServiceData?.result?.find(
                (u: any) => String(u.name) === String(service),
              );
              acc[service.toLowerCase()] = {
                enabled: true,
                threshold: String(rate.threshold),
                threshold_unit: utilityObj?.utilityUnit ?? "NA",
                below_rate_id: String(rate.tariffBelow),
                above_rate_id: String(rate.tariffAbove),
                avg_consumption: String(rate.avgConsumption || 0),
              };
            }
            return acc;
          },
          {},
        ),
      },
      charges_config: {
        linked_charges: (charges.linkedCharges || []).map((c: any) => ({
          name: c.name,
          linked_service: c.linkedService,
          charge_rate: parseFloat(c.chargeUnit) || 0,
          unit: c.usage || "$/KL",
          usage: c.usage,
        })),
        fixed_charges: (charges.fixedCharges || []).map((c: any) => {
          const fcMode = charges.fixedChargeMode || c.mode || "common";
          const isParamWise =
            fcMode === "parameter-wise" || fcMode === "parameter_wise";
          return {
            mode: isParamWise ? "parameter_wise" : "common",
            name: c.chargeName || c.name,
            amount: parseFloat(c.amount) || 0,
            include_in: c.includeIn || "every",
            ...(isParamWise
              ? {
                  utility_services: c.utilityService
                    ? Array.isArray(c.utilityService)
                      ? c.utilityService.map((v: any) =>
                          typeof v === "object" ? v.value : v,
                        )
                      : [
                          typeof c.utilityService === "object"
                            ? c.utilityService.value
                            : c.utilityService,
                        ]
                    : c.utility_services || [],
                  parameter_id: parseInt(c.parameter) || null,
                  parameter_code:
                    c.code || c.parameter_code || c.parameterCode || "",
                  parameter_values: c.value
                    ? Array.isArray(c.value)
                      ? c.value.map((v: any) =>
                          typeof v === "object" ? v.value : v,
                        )
                      : [typeof c.value === "object" ? c.value.value : c.value]
                    : [],
                }
              : {}),
          };
        }),
        surcharges: (charges.surcharges || []).map((c: any) => {
          const scMode = charges.surchargeMode || c.mode || "common";
          const isParamWise =
            scMode === "parameter-wise" || scMode === "parameter_wise";
          const surchargeRate = parseFloat(c.rate) || 0;
          const surchargeAmt = parseFloat(c.amount) || surchargeRate;
          return {
            mode: isParamWise ? "parameter_wise" : "common",
            name: c.surchargeName || c.name,
            type: c.type || "percentage",
            rate: surchargeRate,
            amount: surchargeAmt,
            ...(isParamWise
              ? {
                  utility_services: c.utilityService
                    ? Array.isArray(c.utilityService)
                      ? c.utilityService.map((v: any) =>
                          typeof v === "object" ? v.value : v,
                        )
                      : [
                          typeof c.utilityService === "object"
                            ? c.utilityService.value
                            : c.utilityService,
                        ]
                    : c.utility_services || [],
                  parameter_id: parseInt(c.parameter) || null,
                  parameter_code:
                    c.code || c.parameter_code || c.parameterCode || "",
                  parameter_values: c.value
                    ? Array.isArray(c.value)
                      ? c.value.map((v: any) =>
                          typeof v === "object" ? v.value : v,
                        )
                      : [typeof c.value === "object" ? c.value.value : c.value]
                    : [],
                }
              : {}),
          };
        }),
        taxes: (charges.taxes || []).map((c: any) => {
          const tMode = charges.taxMode || c.mode || "common";
          const isParamWise =
            tMode === "parameter-wise" || tMode === "parameter_wise";
          const taxRate = parseFloat(c.rate) || 0;
          const taxAmt = parseFloat(c.amount) || taxRate;
          const paramVals = c.value
            ? Array.isArray(c.value)
              ? c.value
                  .flat()
                  .map((v: any) =>
                    typeof v === "object" && v !== null
                      ? (v.value ?? v.key)
                      : v,
                  )
              : [
                  typeof c.value === "object" && c.value !== null
                    ? (c.value.value ?? c.value.key)
                    : c.value,
                ]
            : Array.isArray(c.parameter_values)
              ? c.parameter_values.flat()
              : [];
          return {
            mode: isParamWise ? "parameter_wise" : "common",
            name: c.taxName || c.name,
            type: c.type?.toLowerCase() || "percentage",
            rate: taxRate,
            amount: taxAmt,
            priority: c.priority || 1,
            ...(isParamWise
              ? {
                  utility_services: c.utilityService
                    ? Array.isArray(c.utilityService)
                      ? c.utilityService.map((v: any) =>
                          typeof v === "object" ? v.value : v,
                        )
                      : [
                          typeof c.utilityService === "object"
                            ? c.utilityService.value
                            : c.utilityService,
                        ]
                    : c.utility_services || [],
                  parameter_id: parseInt(c.parameter || c.parameter_id) || null,
                  parameter_code:
                    c.code || c.parameter_code || c.parameterCode || "",
                  parameter_values: paramVals,
                }
              : {}),
          };
        }),
      },
      deposits_config: (() => {
        const depRaw = allStepData?.[4] || allStepData?.deposits;
        const depApi =
          planDetail?.result?.depositsConfig ||
          planDetail?.result?.deposits_config ||
          planDetail?.result;

        const enabled =
          depRaw?.requireDeposit !== undefined
            ? depRaw.requireDeposit
            : depRaw?.enabled !== undefined
              ? depRaw.enabled
              : depApi?.deposits_config?.enabled !== undefined
                ? depApi.deposits_config.enabled
                : depApi?.depositsConfig?.enabled !== undefined
                  ? depApi.depositsConfig.enabled
                  : depApi?.enabled !== undefined
                    ? depApi.enabled
                    : false;

        const rules: any[] = [];

        if (enabled) {
          const mode =
            depRaw?.mode ||
            depApi?.mode ||
            depApi?.rules_grouped?.[0]?.rules?.[0]?.mode ||
            "common";

          if (depRaw?.deposits) {
            if (mode === "common" && depRaw.deposits.common) {
              const r = depRaw.deposits.common;
              const isPayInt =
                r.payInterest === "yes" || r.payInterest === true;
              rules.push({
                id: "dep-001",
                mode: "common",
                deposit_name: r.depositName || r.deposit_name || "",
                basis: "flat",
                amount: Number(r.depositAmount || r.amount || 0),
                multiplier: null,
                pay_interest: isPayInt,
                eligible_after: Number(
                  r.eligibleAfter || r.eligible_after || 0,
                ),
                eligible_after_unit:
                  r.eligiblePeriod || r.eligible_after_unit || "months",
                interest_tiers: isPayInt
                  ? (r.tiers || r.interestTiers || r.interest_tiers || []).map(
                      (t: any) => ({
                        from_month: Number(t.from ?? t.from_month ?? 0),
                        to_month:
                          t.to !== undefined && t.to !== null && t.to !== ""
                            ? Number(t.to)
                            : t.to_month !== undefined &&
                                t.to_month !== null &&
                                t.to_month !== ""
                              ? Number(t.to_month)
                              : null,
                        rate: Number(t.rate || 0),
                      }),
                    )
                  : [],
              });
            } else if (
              (mode === "parameter-wise" || mode === "parameter_wise") &&
              depRaw.deposits.parameterWise?.rules
            ) {
              (depRaw.deposits.parameterWise.rules || []).forEach(
                (rule: any, index: number) => {
                  const paramObj = parameterOptions.find(
                    (p: any) => String(p.id) === String(rule.parameter),
                  );
                  const paramCode =
                    paramObj?.code || rule.code || rule.parameter_code || "";
                  const isPayInt =
                    rule.payInterest === "yes" || rule.payInterest === true;
                  const toStrArr = (v: any): string[] => {
                    if (!v) return [];
                    const arr = Array.isArray(v) ? v.flat() : [v];
                    return arr
                      .map((x: any) =>
                        typeof x === "object" && x !== null
                          ? (x.value ?? x.label ?? String(x))
                          : String(x),
                      )
                      .filter((s) => s && s.toLowerCase() !== "all");
                  };
                  rules.push({
                    id: rule.id || `dep-00${index + 1}`,
                    mode: "parameter_wise",
                    utility_services: toStrArr(
                      rule.utilityService || rule.utility_services,
                    ),
                    parameter_id: rule.parameter
                      ? Number(rule.parameter)
                      : null,
                    parameter_code: paramCode,
                    parameter_values: toStrArr(
                      rule.value || rule.parameter_values,
                    ),
                    deposit_name: rule.depositName || rule.deposit_name || "",
                    basis: "flat",
                    amount: Number(rule.amount || 0),
                    multiplier: null,
                    pay_interest: isPayInt,
                    eligible_after: Number(
                      rule.eligibleAfter || rule.eligible_after || 0,
                    ),
                    eligible_after_unit:
                      rule.eligiblePeriod ||
                      rule.eligible_after_unit ||
                      "months",
                    interest_tiers: isPayInt
                      ? (
                          rule.tiers ||
                          rule.interestTiers ||
                          rule.interest_tiers ||
                          []
                        ).map((t: any) => ({
                          from_month: Number(t.from ?? t.from_month ?? 0),
                          to_month:
                            t.to !== undefined && t.to !== null && t.to !== ""
                              ? Number(t.to)
                              : t.to_month !== undefined &&
                                  t.to_month !== null &&
                                  t.to_month !== ""
                                ? Number(t.to_month)
                                : null,
                          rate: Number(t.rate || 0),
                        }))
                      : [],
                    priority: rule.priority || index + 1,
                  });
                },
              );
            }
          } else {
            const existingRules =
              depApi?.deposits_config?.rules ||
              depApi?.depositsConfig?.rules ||
              depApi?.rules ||
              depApi?.rulesGrouped?.flatMap((g: any) => g.rules) ||
              depApi?.rules_grouped?.flatMap((g: any) => g.rules) ||
              [];
            existingRules.forEach((r: any, idx: number) => {
              const rMode = r.mode || mode;
              const isParamWise =
                rMode === "parameter-wise" || rMode === "parameter_wise";
              const isPayInt =
                r.pay_interest === true ||
                r.payInterest === "yes" ||
                r.payInterest === true;
              rules.push({
                id: r.id || `dep-00${idx + 1}`,
                mode: isParamWise ? "parameter_wise" : "common",
                deposit_name: r.deposit_name || r.depositName || "",
                basis: r.basis || "flat",
                amount: Number(r.amount || r.depositAmount || 0),
                multiplier: r.multiplier || null,
                pay_interest: isPayInt,
                eligible_after: Number(
                  r.eligible_after || r.eligibleAfter || 0,
                ),
                eligible_after_unit:
                  r.eligible_after_unit || r.eligiblePeriod || "months",
                interest_tiers: isPayInt
                  ? r.interest_tiers || r.interestTiers || r.tiers || []
                  : [],
                ...(isParamWise
                  ? {
                      utility_services: Array.isArray(r.utility_services)
                        ? r.utility_services
                        : r.utilityService
                          ? [r.utilityService]
                          : [],
                      parameter_id: r.parameter_id || r.parameter || null,
                      parameter_code: r.parameter_code || r.code || "",
                      parameter_values: Array.isArray(r.parameter_values)
                        ? r.parameter_values
                        : r.value
                          ? [r.value]
                          : [],
                      priority: r.priority || idx + 1,
                    }
                  : {}),
              });
            });
          }
        }

        return { enabled, rules };
      })(),

      penalties_config: (() => {
        const penRaw = allStepData?.[5] || allStepData?.penalties;
        const penApi =
          planDetail?.result?.penaltiesConfig ||
          planDetail?.result?.penalties_config ||
          planDetail?.result;

        const enabled =
          penRaw?.enablePenalties !== undefined
            ? penRaw.enablePenalties
            : penRaw?.enabled !== undefined
              ? penRaw.enabled
              : penApi?.penalties_config?.enabled !== undefined
                ? penApi.penalties_config.enabled
                : penApi?.penaltiesConfig?.enabled !== undefined
                  ? penApi.penaltiesConfig.enabled
                  : penApi?.enabled !== undefined
                    ? penApi.enabled
                    : false;

        const rules: any[] = [];

        if (enabled) {
          const penMode =
            penRaw?.mode ||
            penApi?.mode ||
            penApi?.rulesGrouped?.[0]?.rules?.[0]?.mode ||
            "common";

          if (penRaw?.penalties && Array.isArray(penRaw.penalties)) {
            penRaw.penalties.forEach((item: any, index: number) => {
              const toStrArr = (v: any): string[] => {
                if (!v) return [];
                const arr = Array.isArray(v) ? v.flat() : [v];
                return arr
                  .map((x: any) =>
                    typeof x === "object" && x !== null
                      ? (x.value ?? x.label ?? String(x))
                      : String(x),
                  )
                  .filter((s) => s && s.toLowerCase() !== "all");
              };

              if (penMode === "common") {
                rules.push({
                  id: item.id || `pen-00${index + 1}`,
                  mode: "common",
                  penalty_name: item.penaltyName || item.penalty_name || "",
                  charge_type:
                    item.type || item.chargeType || item.charge_type || "flat",
                  amount: Number(item.amount || 0),
                  grace_days: Number(
                    item.grace || item.graceDays || item.grace_days || 0,
                  ),
                });
              } else {
                const utility_services = toStrArr(
                  item.utilityService || item.utility_services,
                );
                const parameter_values = toStrArr(
                  item.value || item.parameter_values,
                );
                const paramObj = parameterOptions.find(
                  (p: any) => String(p.id) === String(item.parameter),
                );
                const paramCode =
                  paramObj?.code || item.code || item.parameter_code || "";
                rules.push({
                  id: item.id || `pen-00${index + 1}`,
                  mode: "parameter_wise",
                  utility_services,
                  parameter_id: item.parameter ? Number(item.parameter) : null,
                  parameter_code: paramCode,
                  parameter_values,
                  penalty_name: item.penaltyName || item.penalty_name || "",
                  charge_type:
                    item.chargeType || item.charge_type || item.type || "flat",
                  amount: Number(item.amount || 0),
                  grace_days: Number(
                    item.grace || item.graceDays || item.grace_days || 0,
                  ),
                  priority: item.priority || index + 1,
                });
              }
            });
          } else {
            const existingRules =
              penApi?.penalties_config?.rules ||
              penApi?.penaltiesConfig?.rules ||
              penApi?.rules ||
              penApi?.rulesGrouped?.flatMap((g: any) => g.rules) ||
              penApi?.rules_grouped?.flatMap((g: any) => g.rules) ||
              [];
            existingRules.forEach((r: any, idx: number) => {
              const rMode = r.mode || penMode;
              const isParamWise =
                rMode === "parameter-wise" || rMode === "parameter_wise";
              rules.push({
                id: r.id || `pen-00${idx + 1}`,
                mode: isParamWise ? "parameter_wise" : "common",
                penalty_name: r.penalty_name || r.penaltyName || "",
                charge_type: r.charge_type || r.chargeType || r.type || "flat",
                amount: Number(r.amount || 0),
                grace_days: Number(r.grace_days || r.graceDays || r.grace || 0),
                ...(isParamWise
                  ? {
                      utility_services: Array.isArray(r.utility_services)
                        ? r.utility_services
                        : r.utilityService
                          ? [r.utilityService]
                          : [],
                      parameter_id: r.parameter_id || r.parameter || null,
                      parameter_code: r.parameter_code || r.code || "",
                      parameter_values: Array.isArray(r.parameter_values)
                        ? r.parameter_values
                        : r.value
                          ? [r.value]
                          : [],
                      priority: r.priority || idx + 1,
                    }
                  : {}),
              });
            });
          }
        }

        return { enabled, rules };
      })(),
    };
    updatePlan(
      { planId: planId as string, payload },
      {
        onSuccess: () => {
          localStorage.removeItem(`plan-edit-progress-${planId}`);
          if (stepHelpers?.resetAllData) {
            stepHelpers.resetAllData();
          }
          if (onNext) onNext();
        },
      },
    );
  };

  const oldData: any = useMemo(
    () => normalizeData(planDetail?.result, true, planDetailRates?.result),
    [planDetail, planDetailRates, currency],
  );
  const newData: any = useMemo(
    () => normalizeData(allStepData, false),
    [allStepData, currency],
  );

  const { data: categoriesResult } = useCategoryOptions();
  const categoryOptions = useMemo(
    () =>
      Array.isArray(categoriesResult)
        ? categoriesResult
        : (categoriesResult as any)?.result || [],
    [categoriesResult],
  );

  const getCatLabel = (id: string) => {
    const found = categoryOptions.find(
      (c: any) =>
        String(c.id) === String(id) ||
        String(c.code) === String(id) ||
        String(c.value) === String(id),
    );
    return found ? found.name || found.label || found.value || id : id;
  };

  const allSelectedServices = useMemo(() => {
    const services = new Set<string>();
    const addSvc = (r: any) => {
      const svc = r.utility_services || r.utilityService || r.utilityServices;
      if (typeof svc === "string") {
        svc.split(",").forEach((s: string) => {
          const trimmed = s.trim();
          if (trimmed) services.add(trimmed);
        });
      } else if (Array.isArray(svc)) {
        svc.forEach((s: any) => {
          const str =
            typeof s === "object" && s !== null
              ? (s.value ?? s.label ?? String(s))
              : String(s);
          if (str && str.trim()) services.add(str.trim());
        });
      }
    };
    newData.charges.fixedCharges?.forEach((f: any) => addSvc(f));
    newData.charges.surcharges?.forEach((s: any) => addSvc(s));
    newData.penalties.rules?.forEach(addSvc);
    newData.deposits.rules?.forEach(addSvc);
    oldData.charges.fixedCharges?.forEach((f: any) => addSvc(f));
    oldData.charges.surcharges?.forEach((s: any) => addSvc(s));
    oldData.penalties.rules?.forEach(addSvc);
    oldData.deposits.rules?.forEach(addSvc);
    return Array.from(services).filter((s) => s && s !== "-" && s !== "All");
  }, [newData, oldData]);

  const { data: parameterResult } = useGetParameter(allSelectedServices);
  const { data: globalParameterResult } = useGetParameter();

  const parameterOptions = useMemo(() => {
    const list1 = Array.isArray(parameterResult)
      ? parameterResult
      : parameterResult?.result || parameterResult?.data || [];
    const list2 = Array.isArray(globalParameterResult)
      ? globalParameterResult
      : globalParameterResult?.result || globalParameterResult?.data || [];
    const combined = [
      ...(Array.isArray(list1) ? list1 : []),
      ...(Array.isArray(list2) ? list2 : []),
    ];
    const seen = new Set<string>();
    const res: any[] = [];
    combined.forEach((p: any) => {
      const id = String(p.id ?? p.parameter_id ?? p.code ?? p.name);
      if (id && !seen.has(id)) {
        seen.add(id);
        res.push(p);
      }
    });
    return res;
  }, [parameterResult, globalParameterResult]);

  const getParamLabel = (id: any) => {
    if (id === undefined || id === null || id === "" || id === "-") return "-";
    const strId = String(id).trim();
    const found = parameterOptions.find(
      (p: any) =>
        String(p.id).trim() === strId ||
        String(p.parameter_id).trim() === strId ||
        String(p.code).trim() === strId ||
        String(p.parameter_code).trim() === strId ||
        String(p.name).trim() === strId ||
        String(p.parameter_name).trim() === strId,
    );
    return found
      ? found.name || found.parameter_name || found.label || found.code || strId
      : strId;
  };

  const ratesTableData = useMemo(() => {
    const oldRatesKeys = Object.keys(oldData.rates || {});
    const newRatesKeys = Object.keys(newData.rates || {});
    const allServices = Array.from(new Set([...oldRatesKeys, ...newRatesKeys]));

    const formatRate = (rateObj: any) => {
      if (!rateObj) return "";
      if (Array.isArray(rateObj))
        return rateObj
          .map((r) => r?.rateName || r?.name || r?.tariffName || String(r))
          .join(", ");
      if (typeof rateObj === "string") return rateObj;
      if (rateObj.tariffBelowName || rateObj.tariffAboveName) {
        return `Below Thr: ${rateObj.tariffBelowName || "-"} | Above Thr: ${rateObj.tariffAboveName || "-"}`;
      }
      if (rateObj.tariffName) return rateObj.tariffName;
      if (rateObj.tariff) return String(rateObj.tariff);
      return JSON.stringify(rateObj);
    };

    return allServices.map((service) => {
      const oldR = (oldData.rates as any)[service];
      const newR = (newData.rates as any)[service];
      const oldAvg =
        oldR?.avgConsumption != null && oldR?.avgConsumption !== ""
          ? String(oldR.avgConsumption)
          : "-";
      const newAvg =
        newR?.avgConsumption != null && newR?.avgConsumption !== ""
          ? String(newR.avgConsumption)
          : "-";

      return {
        old: {
          service,
          rates: formatRate(oldR),
          avgConsumption: oldAvg,
        },
        new: {
          service,
          rates: formatRate(newR),
          avgConsumption: newAvg,
        },
      };
    });
  }, [oldData, newData]);

  const basicInfoTableData = useMemo(() => {
    // If BasicInfo step was never visited/changed, allStepData[0] won't have plan fields.
    // Use old data values for new to suppress a false diff.
    const basicStepData = allStepData?.[0] || allStepData?.["basic-info"] || allStepData?.basic;
    const hasBasicData =
      basicStepData &&
      (basicStepData.planName !== undefined ||
        basicStepData.planCode !== undefined ||
        basicStepData.validateForm !== undefined);

    const nb = hasBasicData ? newData.basic : oldData.basic;

    return [
      {
        old: { field: "Plan Name", value: oldData.basic.planName },
        new: { field: "Plan Name", value: nb.planName },
      },
      {
        old: { field: "Plan Code", value: oldData.basic.planCode },
        new: { field: "Plan Code", value: nb.planCode },
      },
      {
        old: { field: "Effective Date", value: oldData.basic.effectiveDate },
        new: { field: "Effective Date", value: nb.effectiveDate },
      },
      {
        old: { field: "End Date", value: oldData.basic.endDate },
        new: { field: "End Date", value: nb.endDate },
      },
    ];
  }, [oldData.basic, newData.basic, allStepData]);


  const categoriesTableData = useMemo(() => {
    const formatCats = (cats: any[]) =>
      Array.from(
        new Set(
          (cats || [])
            .map((c) => {
              if (!c) return "";
              if (typeof c === "string") return getCatLabel(c);
              return c.name || c.label || c.code || String(c);
            })
            .filter(Boolean),
        ),
      )
        .sort()
        .join(", ") || "None";

    // If plan-config step was never visited/changed, allStepData[1] won't have category keys.
    // In that case, treat new = old to suppress a false diff.
    const catStepData = allStepData?.[1] || allStepData?.["plan-config"] || allStepData?.categories;
    const hasCatData =
      catStepData &&
      (catStepData.consumerCategories !== undefined ||
        catStepData.categories !== undefined ||
        catStepData.subCategories !== undefined);

    const newCats = hasCatData
      ? newData.categories.categories
      : oldData.categories.categories;
    const newSubCats = hasCatData
      ? newData.categories.subCategories
      : oldData.categories.subCategories;

    return [
      {
        old: {
          field: "Categories",
          value: formatCats(oldData.categories.categories),
        },
        new: {
          field: "Categories",
          value: formatCats(newCats),
        },
      },
      {
        old: {
          field: "Sub-categories",
          value: formatCats(oldData.categories.subCategories),
        },
        new: {
          field: "Sub-categories",
          value: formatCats(newSubCats),
        },
      },
    ];
  }, [oldData.categories, newData.categories, categoryOptions, allStepData]);


  // Change detection helpers
  const hasChangesCard = (data: any[]) => {
    const format = (v: any) => {
      if (v === undefined || v === null || v === "") return undefined;
      if (Array.isArray(v)) return v.join(", ");
      return String(v);
    };
    return data.some(
      (item) => format(item.old.value) !== format(item.new.value),
    );
  };

  const hasChangesTable = (data: any[], columns: any[]) => {
    const normalizeForCompare = (v: any): string => {
      if (v === undefined || v === null || v === "") return "";
      if (Array.isArray(v)) return v.map((x) => String(x).trim()).join(", ");
      return String(v).trim();
    };
    return data.some((row: any) =>
      columns.some(
        (c: any) =>
          normalizeForCompare(row.old?.[c.key]) !==
          normalizeForCompare(row.new?.[c.key]),
      ),
    );
  };

  const ratesColumns = [
    { key: "service", label: "Service" },
    { key: "rates", label: "Selected Rates" },
    { key: "avgConsumption", label: "Avg Consumption" },
  ];

  const linkedChargesColumns = [
    { key: "name", label: "Name" },
    { key: "linkedService", label: "Linked Service" },
    { key: "usage", label: "Usage (%)" },
    { key: "chargeUnit", label: "Rate ($/Unit)" },
  ];

  const taxIsParamWise =
    newData.charges.taxMode === "parameter-wise" ||
    newData.charges.taxMode === "parameter_wise" ||
    newData.charges.taxMode === "parameterWise" ||
    oldData.charges.taxMode === "parameter-wise" ||
    oldData.charges.taxMode === "parameter_wise" ||
    oldData.charges.taxMode === "parameterWise";

  const taxesColumns = [
    ...(taxIsParamWise
      ? [
          { key: "utilityService", label: "Utility Service" },
          { key: "parameter", label: "Parameter" },
          { key: "value", label: "Value" },
        ]
      : []),
    { key: "taxName", label: "Tax Name" },
    { key: "type", label: "Type" },
    { key: "rate", label: "Rate" },
  ];

  const fixedIsParamWise =
    newData.charges.fixedChargeMode === "parameter-wise" ||
    newData.charges.fixedChargeMode === "parameter_wise" ||
    newData.charges.fixedChargeMode === "parameterWise" ||
    oldData.charges.fixedChargeMode === "parameter-wise" ||
    oldData.charges.fixedChargeMode === "parameter_wise" ||
    oldData.charges.fixedChargeMode === "parameterWise";

  const fixedChargesColumns = [
    ...(fixedIsParamWise
      ? [
          { key: "utilityService", label: "Utility Service" },
          { key: "parameter", label: "Parameter" },
          { key: "value", label: "Value" },
        ]
      : []),
    { key: "chargeName", label: "Charge Name" },
    { key: "amount", label: "Amount" },
    { key: "includeIn", label: "Include In" },
  ];

  const surchargeIsParamWise =
    newData.charges.surchargeMode === "parameter-wise" ||
    newData.charges.surchargeMode === "parameter_wise" ||
    newData.charges.surchargeMode === "parameterWise" ||
    oldData.charges.surchargeMode === "parameter-wise" ||
    oldData.charges.surchargeMode === "parameter_wise" ||
    oldData.charges.surchargeMode === "parameterWise";

  const surchargesColumns = [
    ...(surchargeIsParamWise
      ? [
          { key: "utilityService", label: "Utility Service" },
          { key: "parameter", label: "Parameter" },
          { key: "value", label: "Value" },
        ]
      : []),
    { key: "surchargeName", label: "Name" },
    { key: "type", label: "Type" },
    { key: "rate", label: "Rate" },
  ];

  const penaltyColumns = [
    ...(newData.penalties.mode === "parameter-wise" ||
    newData.penalties.mode === "parameter_wise"
      ? [
          { key: "utility_services", label: "Utility Service" },
          { key: "parameter", label: "Parameter" },
          { key: "parameter_values", label: "Value" },
        ]
      : []),
    { key: "penalty_name", label: "Penalty Name" },
    { key: "charge_type", label: "Type" },
    { key: "grace_days", label: "Grace Days" },
    { key: "amount", label: "Amount" },
  ];

  const depositColumns = [
    ...(newData.deposits.mode === "parameter-wise" ||
    newData.deposits.mode === "parameter_wise"
      ? [
          { key: "utility_services", label: "Utility Service" },
          { key: "parameter", label: "Parameter" },
          { key: "parameter_values", label: "Value" },
        ]
      : []),
    { key: "deposit_name", label: "Deposit Name" },
    { key: "amount", label: "Amount" },
    { key: "pay_interest", label: "Pay Interest" },
    { key: "eligible_after", label: "Eligible After" },
  ];

  const linkedData = pairArraysByName(
    oldData.charges.linkedCharges,
    newData.charges.linkedCharges,
    (l) => l?.name || l?.linkedService || l?.linked_service || "",
  );

  const mappedTaxes = newData.charges.taxes.map((f: any) => ({
    ...f,
    parameter: getParamLabel(f.parameter_id || f.parameter),
  }));
  const mappedOldTaxes = oldData.charges.taxes.map((f: any) => ({
    ...f,
    parameter: getParamLabel(f.parameter_id || f.parameter),
  }));
  const taxesData = pairArraysByName(
    mappedOldTaxes,
    mappedTaxes,
    (t) => t?.taxName || t?.tax_name || t?.name || "",
  );

  const mappedFixedCharges = newData.charges.fixedCharges.map((f: any) => ({
    ...f,
    parameter: getParamLabel(f.parameter_id || f.parameter),
  }));
  const mappedOldFixedCharges = oldData.charges.fixedCharges.map((f: any) => ({
    ...f,
    parameter: getParamLabel(f.parameter_id || f.parameter),
  }));
  const fixedChargesData = pairArraysByName(
    mappedOldFixedCharges,
    mappedFixedCharges,
    (f) => f?.chargeName || f?.charge_name || f?.name || "",
  );

  const mappedSurcharges = newData.charges.surcharges.map((f: any) => ({
    ...f,
    parameter: getParamLabel(f.parameter_id || f.parameter),
  }));
  const mappedOldSurcharges = oldData.charges.surcharges.map((f: any) => ({
    ...f,
    parameter: getParamLabel(f.parameter_id || f.parameter),
  }));
  const surchargesData = pairArraysByName(
    mappedOldSurcharges,
    mappedSurcharges,
    (s) => s?.surchargeName || s?.surcharge_name || s?.chargeName || s?.name || "",
  );

  const mappedPenalties = newData.penalties.rules.map((r: any) => ({
    ...r,
    parameter: getParamLabel(r.parameter_id || r.parameter),
  }));
  const mappedOldPenalties = oldData.penalties.rules.map((r: any) => ({
    ...r,
    parameter: getParamLabel(r.parameter_id || r.parameter),
  }));
  const penaltiesData = pairArraysByName(
    mappedOldPenalties,
    mappedPenalties,
    (p) => p?.penalty_name || p?.penaltyName || p?.name || "",
  );

  const mappedDeposits = newData.deposits.rules.map((r: any) => ({
    ...r,
    parameter: getParamLabel(r.parameter_id || r.parameter),
  }));
  const mappedOldDeposits = oldData.deposits.rules.map((r: any) => ({
    ...r,
    parameter: getParamLabel(r.parameter_id || r.parameter),
  }));
  const depositsData = pairArraysByName(
    mappedOldDeposits,
    mappedDeposits,
    (d) => d?.deposit_name || d?.depositName || d?.name || "",
  );

  const showChargesSection =
    hasChangesTable(linkedData, linkedChargesColumns) ||
    hasChangesTable(taxesData, taxesColumns) ||
    hasChangesTable(fixedChargesData, fixedChargesColumns) ||
    hasChangesTable(surchargesData, surchargesColumns);

  return (
    <div className="w-full space-y-6">
      <div>
        <h2 className="text-2xl font-semibold mb-1 text-gray-900">
          Review & Change Impact
        </h2>
        <p className="text-sm text-gray-500 mb-6">
          Review the differences between the current plan and your new
          configuration.
        </p>
      </div>

      <div className="mt-8 space-y-8">
        {/* Basic Info & Categories */}
        {hasChangesCard(basicInfoTableData) && (
          <div>
            <h3 className="text-lg font-semibold text-gray-800 mb-4">
              Basic Information
            </h3>
            <DiffCard title="Plan Details" data={basicInfoTableData} />
          </div>
        )}

        {hasChangesCard(categoriesTableData) && (
          <div>
            <h3 className="text-lg font-semibold text-gray-800 mb-4">
              Consumer Categories
            </h3>
            <DiffCard title="Assigned Categories" data={categoriesTableData} />
          </div>
        )}

        {/* Select Rates */}
        {hasChangesTable(ratesTableData, ratesColumns) && (
          <div>
            <h3 className="text-lg font-semibold text-gray-800 mb-4">
              Select Rates
            </h3>
            <DiffTable
              title="Service Rates"
              data={ratesTableData}
              columns={ratesColumns}
            />
          </div>
        )}

        {/* Charges & Taxes */}
        {showChargesSection && (
          <div>
            <h3 className="text-lg font-semibold text-gray-800 mb-4">
              Charges & Taxes
            </h3>
            <DiffTable
              title="Linked charges"
              data={linkedData}
              columns={linkedChargesColumns}
            />
            <DiffTable
              title="Taxes"
              mode={newData.charges.taxMode || "common"}
              data={taxesData}
              columns={taxesColumns}
            />
            <DiffTable
              title="Fixed charges"
              mode={newData.charges.fixedChargeMode || "parameterWise"}
              data={fixedChargesData}
              columns={fixedChargesColumns}
            />
            <DiffTable
              title="Surcharges"
              mode={newData.charges.surchargeMode || "common"}
              data={surchargesData}
              columns={surchargesColumns}
            />
          </div>
        )}

        {/* Penalties */}
        {hasChangesTable(penaltiesData, penaltyColumns) && (
          <div>
            <h3 className="text-lg font-semibold text-gray-800 mb-4">
              Penalties
            </h3>
            <DiffTable
              title="Penalty Rules"
              mode={newData.penalties.mode}
              data={penaltiesData}
              columns={penaltyColumns}
            />
          </div>
        )}

        {/* Deposits */}
        {hasChangesTable(depositsData, depositColumns) && (
          <div>
            <h3 className="text-lg font-semibold text-gray-800 mb-4">
              Deposits
            </h3>
            <DiffTable
              title="Deposit Rules"
              mode={newData.deposits.mode}
              data={depositsData}
              columns={depositColumns}
            />
          </div>
        )}

        {!(
          hasChangesCard(basicInfoTableData) ||
          hasChangesCard(categoriesTableData) ||
          hasChangesTable(ratesTableData, ratesColumns) ||
          showChargesSection ||
          hasChangesTable(penaltiesData, penaltyColumns) ||
          hasChangesTable(depositsData, depositColumns)
        ) && (
          <div className="p-8 text-center bg-gray-50 border border-dashed border-gray-300 rounded-xl my-6">
            <p className="text-gray-700 font-medium text-base">
              No Configuration Changes Detected
            </p>
            <p className="text-gray-500 text-xs mt-1">
              All settings match the current plan configuration. Modify values in preceding steps to review changes here.
            </p>
          </div>
        )}

      </div>

      {/* Footer */}
      <div className="flex items-center justify-between mt-12 border-t pt-6">
        <div>
          {!isFirstStep && onPrevious && (
            <Button type="button" variant="outline" onClick={onPrevious}>
              Previous
            </Button>
          )}
        </div>
        <div className="flex gap-3">
          <Link
            to="/plan-tarrif/plan-tarrif-landing?tab=plans"
            className={buttonVariants({ variant: "outline" })}
          >
            Cancel
          </Link>
          <Button
            type="button"
            onClick={handleSaveChanges}
            disabled={isPending}
            className="bg-blue-600 hover:bg-blue-700 text-white flex items-center gap-2"
          >
            {isPending ? "Saving..." : "Save Changes"}
            {!isPending && <ArrowRight className="w-4 h-4" />}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Review;
